import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ProjectTrain,
  ProjectTrainDetail,
  ProjectModelItem,
  initialState,
} from "../../types/interface/ProjectTrain.interface";
import AxiosInstance from "../../Service/AxiosInstance";
import { DataIdDetails } from "../../pages/Train/TrainContext";
import { toast } from "react-toastify";

// Create an async thunk for the first API call
export const fetchProjectTrain = createAsyncThunk(
  "projectTrain/fetchAll",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get<ProjectTrain[]>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/GetAll?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      // If the error is a plain string, set it directly
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        // Check if the response is a string
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchProjectTrainAll = createAsyncThunk(
  "ProjectTrain/GetAllTrainingDetails",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get<ProjectTrain[]>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/GetAllTrainingDetails?projectId=${projectId}`
      );
      return response.data;
    } catch (error: any) {
      // If the error is a plain string, set it directly
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        // Check if the response is a string
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Get confusion matrix based on id
export const fetchConfusionMatrix = createAsyncThunk(
  "projectTrain/fetchConfusionMatrix",
  async (
    { trainingId, imageType }: { trainingId: string; imageType: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/DownloadCMReport?trainingId=${trainingId}&imgName=${imageType}`,
        { responseType: "arraybuffer" }
      );

      // Decode response to string
      const decoder = new TextDecoder("utf-8");
      const responseText = decoder.decode(response.data);

      // Check if the response is "Image does not exist"
      if (responseText === "Image does not exist") {
        return "Image does not exist";
      } else {
        // Convert the buffer to a blob and return the URL
        const blob = new Blob([response.data], { type: "image/png" }); // Adjust type if needed
        const imageUrl = URL.createObjectURL(blob);
        return imageUrl;
      }
      //return response.data; // Adjust this if the structure is different
    } catch (error: any) {
      // If the error is a plain string, set it directly
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        // Check if the response is a string
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchModels = createAsyncThunk(
  "projectTrain/fetchModels",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ControlPanel/GetModels?ProjectId=${projectId}`
      );
      return response.data; // Adjust this if the structure is different
    } catch (error: any) {
      // If the error is a plain string, set it directly
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        // Check if the response is a string
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchLabelCount = createAsyncThunk(
  "projectTrain/fetchLabelCount",
  async (projectId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectLabel/GetLabelCount?projectId=${projectId}`
      );
      return response.data; // Adjust this if the structure of response differs
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteProjectTrain = createAsyncThunk(
  "projectTrain/deleteProjectTrain",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/Delete?id=${id}` // Sending the ID in the request body
      );
      return response.data; // Adjust if the structure is different
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteProjectTrainPublishedModel = createAsyncThunk(
  "ProjectTrain/DeletePublishModel",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/DeletePublishModel?id=${id}` // Sending the ID in the request body
      );
      return response.data; // Adjust if the structure is different
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Create an async thunk for adding a rename model
/* export const reNameModelById = createAsyncThunk(
  "projectTrain/reNameModelById",
  async ({ id: any; name: any }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/UpdateModels`,
        newProjectTrainData
      );
      return response.data; // This assumes a successful response contains the data.
    } catch (error: any) {
      // If the error is a plain string, set it directly
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        // Check if the response is a string
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
); */

export const reNameModelById = createAsyncThunk(
  "projectTrain/reNameModelById",
  async ({ id, name }: { id: string; name: string }, { rejectWithValue }) => {
    try {
      // Define the data to be posted
      const reNameModelData = { id, name };

      // Make the POST request
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/UpdateModels`,
        reNameModelData
      );

      return response.data; // Return the response data if the request is successful
    } catch (error: any) {
      // Capture and return error details
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage =
          error.response.data.message ||
          error.response.data ||
          error.message ||
          errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const reNameModelByIdPublished = createAsyncThunk(
  "ProjectTrain/UpdatePublishModel",
  async (
    { id, TrainingName }: { id: string; TrainingName: string },
    { rejectWithValue }
  ) => {
    try {
      const reNameModelData = { id, TrainingName };

      // Make the API request
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/UpdatePublishModel`, 
        reNameModelData
      );

      return response.data; // Return data if successful
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      let errorTitle = "Error";

      if (error.response) {
        errorTitle = error.response.data?.title || errorTitle;
        errorMessage =
          error.response.data?.detail ||  // Using 'detail' as per RFC 9110
          error.response.data?.message || 
          error.response.data || 
          error.message || 
          errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }

      return rejectWithValue({ title: errorTitle, message: errorMessage });
    }
  }
);

// Create an async thunk for fetching project training detail by ID
export const fetchProjectTrainById = createAsyncThunk(
  "projectTrain/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get<ProjectTrainDetail>(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/GetById?id=${id}`
      );
      return response.data; // Adjust this if the structure is different
    } catch (error: any) {
      // If the error is a plain string, set it directly
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        // Check if the response is a string
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchTrainingStatus = createAsyncThunk(
  "projectTrain/fetchTrainingStatus",
  async (projectId: any, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/GetTrainingStatus?projectId=${projectId}`
      );
      return response.data; // This assumes a successful response contains the array of status objects.
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Create an async thunk for adding a new project train
export const addNewProjectTrain = createAsyncThunk(
  "projectTrain/addNew",
  async (newProjectTrainData: DataIdDetails, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/AddNew`,
        newProjectTrainData
      );
      return response.data; // This assumes a successful response contains the data.
    } catch (error: any) {
      // If the error is a plain string, set it directly
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        // Check if the response is a string
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateProjectTrain = createAsyncThunk(
  "projectTrain/updateProjectTrain",
  async ({ id, data }: { id: any; data: any }, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.post(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/Update?id=${id}`,
        data
      );
      return response.data; // Assuming a successful response contains the updated data
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const removeTraining = createAsyncThunk(
  "projectTrain/removeTraining",
  async (trainingId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/RemoveTraining?TrainingId=${trainingId}`
      );
      return response.data; // Adjust this if the structure of response differs
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const publishModel = createAsyncThunk(
  "ProjectTrain/PublishModel",
  async (trainingId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/PublishModel?TrainingId=${trainingId}`
      );
      return response.data; // Adjust this if the structure of response differs
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const publishModeltoInference = createAsyncThunk(
  "ProjectTrain/publishModeltoInference",
  async (trainingId: string, { rejectWithValue }) => {
    try {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/ProjectTrain/PublishInferenceServer?TrainingId=${trainingId}`
      );
      return response.data; // Adjust this if the structure of response differs
    } catch (error: any) {
      let errorMessage = "An unknown error occurred";
      if (error.response) {
        errorMessage = error.response.data || error.message || errorMessage;
      } else {
        errorMessage = error.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);



//Fetch All trained Models
export const fetchAllModels = createAsyncThunk<
  ProjectModelItem[],
  void,
  { rejectValue: string }
>("projectTrain/allModels", async () => {
  try {
    const response = await AxiosInstance.get(
      // `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/GetCompletedModels`
      `${process.env.REACT_APP_SERVER_BASEURL}/api/ProjectTrain/GetPublishModel`
    );

    // const processedData = response.data.map(
    //   (item: Omit<ProjectModelItem, "isChecked">) => ({
    //     ...item,
    //     isChecked: false,
    //   })
    // );

    return response.data;
  } catch (error: any) {
    let errorMessage = "An unknown error occurred";
    if (error.response) {
      errorMessage = error.response.data || error.message || errorMessage;
    } else {
      errorMessage = error.message || errorMessage;
    }
    return errorMessage;
  }
});

export const downloadModels = createAsyncThunk<
  void,
  string[],
  { rejectValue: string }
>("projectTrain/downloadModels", async (trainingIds, thunkAPI) => {
  try {
    const downloadPromises = trainingIds.map(async (trainingId) => {
      const response = await AxiosInstance.get(
        `${process.env.REACT_APP_SERVER_BASEURL}/api/Publish/Download`,
        {
          params: { trainingId },
          responseType: "blob",
        }
      );

      const blob = await response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const a = document.createElement("a");
      a.href = url;
      console.log("urls----------", blob);
      // a.download = "model.zip";
      a.download = `${trainingId}.zip`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // const blob = await response.data;
      // const url = window.URL.createObjectURL(new Blob([blob]));

      // // Extract the last parameter from the URL
      // const lastParameter = url.split("/").pop() || "default-filename.zip"; // Fallback to a default name if extraction fails

      // const a = document.createElement("a");
      // a.href = url;
      // a.download = lastParameter; // Use the last parameter as the file name
      // a.style.display = "none"; // Keep the anchor element hidden
      // document.body.appendChild(a);
      // a.click();

      // document.body.removeChild(a);
      // window.URL.revokeObjectURL(url);

      // const blob = new Blob([response.data], {
      //   type: response.headers["content-type"],
      // });
      // const url = window.URL.createObjectURL(blob);
      // const link = document.createElement("a");
      // link.href = url;
      // link.setAttribute("download", `model_${trainingId}.zip`);
      // document.body.appendChild(link);
      // link.click();
      // link.remove();
    });

    await Promise.all(downloadPromises);
  } catch (error: any) {
    toast.error("failed to download");
    return thunkAPI.rejectWithValue(
      error.message || "Failed to download models"
    );
  }
});

// Create a slice
const projectTrainSlice = createSlice({
  name: "projectTrain",
  initialState,
  reducers: {
    clearModelData: (state) => {
      state.dataModels = [];
    },
  },
  extraReducers: (builder) => {
    // Handle fetchProjectTrain cases
    builder
      .addCase(fetchProjectTrain.pending, (state:any) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectTrain.fulfilled, (state, action) => {
        state.loading = false;
        state.dataModels = action.payload;
      })
      .addCase(fetchProjectTrain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
       // Handle fetchModels cases
      .addCase(fetchProjectTrainAll.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectTrainAll.fulfilled, (state, action) => {
        state.loading = false;
        state.dataModels = action.payload;
      })
      .addCase(fetchProjectTrainAll.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Handle fetchModels cases
      .addCase(fetchModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        state.loading = false;
        state.models = action.payload; // Adjust accordingly
      })
      .addCase(fetchModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.models = []; // Initialize or reset models on error
      })
      // Handle fetchProjectTrainById cases
      .addCase(fetchProjectTrainById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectTrainById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProjectTrain = action.payload;
      })
      .addCase(fetchProjectTrainById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.selectedProjectTrain = null; // Reset or initialize on error
      })

      // .addCase(addNewProjectTrain.pending, (state) => {
      //   state.loading = true;
      //   state.error = null;
      // })
      // .addCase(addNewProjectTrain.fulfilled, (state, action) => {
      //   state.loading = false;
      //   // Assuming you may want to add the new project train to the list
      //   state.dataModels = [...state.dataModels, action.payload];
      // })
      // .addCase(addNewProjectTrain.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload as string; // action.payload should contain the error message
      // })

      .addCase(fetchTrainingStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrainingStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.trainingStatus = action.payload; // Store the fetched training status in state
      })
      .addCase(fetchTrainingStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.trainingStatus = []; // Reset or initialize on error
      })
      .addCase(fetchLabelCount.pending, (state) => {
        state.labelCount = [];
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabelCount.fulfilled, (state, action) => {
        state.loading = false;
        state.labelCount = action.payload; // Assuming `labelCount` is defined in your initial state
      })
      .addCase(fetchLabelCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.labelCount = []; // Reset or initialize on error
      })

      .addCase(fetchAllModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllModels.fulfilled, (state, action) => {
        state.loading = false;
        state.allModels = action.payload;
      })
      .addCase(fetchAllModels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.allModels = [];
      })
      .addCase(updateProjectTrain.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProjectTrain.fulfilled, (state, action) => {
        state.loading = false;
        // Assuming the updated project train data is returned
        // Update the specific project train item in the state
        const updatedData = action.payload;
        state.dataModels = state.dataModels.map((item) =>
          item.id === updatedData.id ? updatedData : item
        );
      })
      .addCase(updateProjectTrain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(downloadModels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadModels.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadModels.rejected, (state, action) => {
        state.loading = false;
        state.error = "failed to download Models";
      });
  },
});

export const { clearModelData } = projectTrainSlice.actions;
// Export the reducer to use it in the store
export default projectTrainSlice.reducer;
